import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { storeWrapper } from '../storeWrapper'

const initialState = {
  comments: [],
  commentsUpdate: 0
}

export const useCommentsStore = storeWrapper('useCommentsStore', (set, get) => ({
  ...initialState,
  createExpensesClaimComment: async ({ expenseClaimId, comment, createdDate, createdBy, updatedDate, updatedBy }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createExpensesClaimComment-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/ExpenseClaimComment/Create`,
              data: {
                expenseClaimId: expenseClaimId ?? null,
                comment: comment ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  createOvertimeClaimComment: async ({ overtimeClaimId, comment, createdDate, createdBy, updatedDate, updatedBy }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createOvertimeClaimComment-${overtimeClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: `/OvertimeClaimComment/Create`,
              data: {
                overtimeClaimId: overtimeClaimId ?? null,
                comment: comment ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  updateExpensesClaimComment: async ({
    expenseClaimCommentId,
    expenseClaimId,
    comment,
    createdDate,
    createdBy,
    updatedDate,
    updatedBy
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateExpensesClaimComment-${expenseClaimCommentId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'accounts',
              endpoint: `/ExpenseClaimComment/Update`,
              data: {
                expenseClaimCommentId: expenseClaimCommentId ?? null,
                expenseClaimId: expenseClaimId ?? null,
                comment: comment ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  updateOvertimeClaimComment: async ({
    overtimeClaimCommentId,
    overtimeClaimId,
    comment,
    createdDate,
    createdBy,
    updatedDate,
    updatedBy
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateOvertimeClaimComment-${overtimeClaimCommentId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'accounts',
              endpoint: `/OvertimeClaimComment/Update`,
              data: {
                overtimeClaimCommentId: overtimeClaimCommentId ?? null,
                overtimeClaimId: overtimeClaimId ?? null,
                comment: comment ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  deleteExpensesClaimComment: async ({ expenseClaimCommentId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteExpensesClaimComment-${expenseClaimCommentId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'accounts',
              endpoint: `/ExpenseClaimComment/Delete`,
              data: {
                id: expenseClaimCommentId
              },
              sendAsUrlParams: true
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  deleteOvertimeClaimComment: async ({ overtimeClaimCommentId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteOvertimeClaimComment-${overtimeClaimCommentId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'accounts',
              endpoint: `/OvertimeClaimComment/Delete`,
              data: {
                id: overtimeClaimCommentId
              },
              sendAsUrlParams: true
            })
            set({ commentsUpdate: get().commentsUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.error('Error deleting overtime claim comment:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllExpensesClaimComments: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllExpensesClaimComments`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/ExpenseClaimComment/GetAll`
            })
            resolve(response)
          } catch (error) {
            console.error('Error fetching all expenses claim comments:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllOvertimeClaimComments: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllOvertimeClaimComments`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/OvertimeClaimComment/GetAll`
            })
            resolve(response)
          } catch (error) {
            console.error('Error fetching all overtime claim comments:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllExpensesClaimCommentsByExpenseClaimId: async ({ expenseClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllExpensesClaimCommentsByExpenseClaimId-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/ExpenseClaimComment/GetAllByExpenseClaimId`,
              data: {
                expenseClaimId: expenseClaimId ?? null
              }
            })
            set({ comments: response })
            resolve(response)
          } catch (error) {
            console.error('Error fetching all expenses claim comments by expense claim ID:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllOvertimeClaimCommentsByOvertimeClaimId: async ({ overtimeClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchAllOvertimeClaimCommentsByOvertimeClaimId-${overtimeClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/OvertimeClaimComment/GetAllByOvertimeClaimId`,
              data: {
                overtimeClaimId: overtimeClaimId ?? null
              }
            })
            set({ comments: response })
            resolve(response)
          } catch (error) {
            console.error('Error fetching all overtime claim comments by overtime claim ID:', error)
            reject(error)
          }
        }
      })
    })
  },
  downloadExpenseClaimReceipt: async ({ expenseClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `downloadExpenseClaimReceipt-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: `/ExpenseClaim/GetReceiptAsFile`,
              data: {
                expenseClaimId: expenseClaimId
              },
              responseType: 'blob'
            })
            resolve(response)
          } catch (error) {
            console.error('Error downloading receipt:', error)
            reject(error)
          }
        }
      })
    })
  },
  reset: () => set(initialState)
}))
